import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { fetchApi } from '../../utilities/api'
import { ENDPOINTS } from '../../utilities/endpoints'
//deploy
export interface Property {
  name: string
  buildingId: string
  ownerId: string
  description: string
  image: string
  price: number
  noOfFloors: number
  floorArea: string
  height: string
  isBuy: boolean
  isFirstTime: boolean
  forSale: boolean
  color: string
  location: {
    lat: number
    lng: number
  }
  status: string
}

interface FetchPropertiesParams {
  limit: number
  offset: number
  userId: string
}

interface FetchPropertyDetailsParams {
  buildingId: string
}

interface GenerateBidParams {
  propertyId: string
  bidAmount: number
}

interface PropertiesState {
  isLoading: boolean
  error: string | null
  totalCount: number
  properties: Property[]

  selectedProperty: Property | null
}

const initialState: PropertiesState = {
  isLoading: false,
  error: null,
  totalCount: 0,
  properties: [],
  selectedProperty: null,
}

export interface BidResponsePayload {
  propertyId:    string
  height:        string
  buildingId:    string
  name:          string
  description:   string
  image:         string
  price:         number
  noOfFloors:    number
  floorArea:     string
  location:      { lat: number; lng: number }
  forSale:       boolean
  locationPoint: {
    type:        'Point'
    coordinates: [number, number]
    _id:         string
  }
  status:        string
  createdBy:     string
  insertDate:    number
}

interface GenerateBidApiResponse {
  apiId:      string
  statusCode: number
  status:     string
  response:BidResponsePayload
}


export const fetchProperties = createAsyncThunk<
  { totalCount: number; properties: Property[] },
  FetchPropertiesParams,
  { rejectValue: string }
>(
  'properties/fetch',
  async ({ limit, offset, userId }, { rejectWithValue }) => {
    try {
      const jwt = localStorage.getItem('token')
      if (!jwt) throw new Error('No auth token found')

      const url = `${ENDPOINTS.PROPERTIES.BASE}?limit=${limit}&offset=${offset}&userId=${userId}`

      const res = await fetchApi<{ totalCount: number; properties: Property[] }>(
        url,
        {
          method: 'GET',
          headers: {
            accept: 'application/json',
            Authorization: jwt,
          },
        }
      )
      return res.data
    } catch (err: any) {
      return rejectWithValue(err.message ?? 'Failed to fetch properties')
    }
  }
)


export const createProperty = createAsyncThunk<
  Property,
  {
    buildingId: string
    name: string
    location: { lat: number; lng: number }
  },
  { rejectValue: string }
>(
  'properties/create',
  async ({ buildingId, name, location }, { rejectWithValue }) => {
    try {
      const jwt = localStorage.getItem('token')
      if (!jwt) throw new Error('No auth token found')

      const res = await fetchApi<Property>(
        ENDPOINTS.BUILDINGS,
        {
          method: 'POST',
          headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: jwt,
          },
          body: JSON.stringify({ buildingId, name, location }),
        }
      )
      return res.data
    } catch (err: any) {
      return rejectWithValue(err.message ?? 'Failed to create property')
    }
  }
)


export const fetchPropertyDetails = createAsyncThunk<
  Property,
  FetchPropertyDetailsParams,
  { rejectValue: string }
>(
  'properties/fetchDetails',
  async ({ buildingId }, { rejectWithValue }) => {
    try {
      const jwt = localStorage.getItem('token')
      if (!jwt) throw new Error('No auth token found')

      const url = `${ENDPOINTS.PROPERTIES.BASE}/checkPropertyDetails?text=${buildingId}`

      const res = await fetchApi<{
        apiId: string
        statusCode: number
        message: string
        data: { propertyDetails: Property[] }
      }>(url, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          Authorization: jwt,
        },
      })

      const list = res.data.data.propertyDetails
      if (!Array.isArray(list) || list.length === 0) {
        throw new Error('No property details returned')
      }
      return list[0]
    } catch (err: any) {
      return rejectWithValue(err.message ?? 'Failed to fetch property details')
    }
  }
)



export const generateBid = createAsyncThunk<
  BidResponsePayload,   
  GenerateBidParams,
  { rejectValue: string }
>(
  'properties/generateBid',
  async ({ propertyId, bidAmount }, { rejectWithValue }) => {
    const token = localStorage.getItem('token')
    if (!token) throw new Error('No auth token found')
    const res = await fetchApi<GenerateBidApiResponse>(
      ENDPOINTS.PROPERTIES.GENERATE_BID,
      {
        method: 'POST',
        headers: {
          'Accept':        'application/json',
          'Content-Type':  'application/json',
          'Authorization': token,
        },
        body: JSON.stringify({ propertyId, bidAmount }),
      }
    )

      return res.data.response


  }
)

const propertiesSlice = createSlice({
  name: 'properties',
  initialState,
  reducers: {
    resetPropertiesState(state) {
      state.isLoading = false
      state.error = null
      state.totalCount = 0
      state.properties = []
      state.selectedProperty = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProperties.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchProperties.fulfilled, (state, action) => {
        state.isLoading = false
        state.totalCount = action.payload.totalCount
        state.properties = action.payload.properties
      })
      .addCase(fetchProperties.rejected, (state, action) => {
        state.isLoading = false
        state.error =
          action.payload ?? action.error.message ?? 'Unknown error'
      })

      .addCase(createProperty.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(createProperty.fulfilled, (state, action) => {
        state.isLoading = false
        state.properties.unshift(action.payload)
        state.totalCount += 1
      })
      .addCase(createProperty.rejected, (state, action) => {
        state.isLoading = false
        state.error =
          action.payload ?? action.error.message ?? 'Failed to create property'
      })

      .addCase(fetchPropertyDetails.pending, (state) => {
        state.isLoading = true
        state.error = null
        state.selectedProperty = null
      })
      .addCase(fetchPropertyDetails.fulfilled, (state, action) => {
        state.isLoading = false
        state.selectedProperty = action.payload
      })
      .addCase(fetchPropertyDetails.rejected, (state, action) => {
        state.isLoading = false
        state.error =
          action.payload ?? action.error.message ?? 'Failed to fetch details'
      })

      builder
      .addCase(generateBid.pending, (state) => {
        state.isLoading = true
        state.error     = null
      })
      .addCase(generateBid.fulfilled, (state, action) => {
        state.isLoading        = false
        state.selectedProperty = {
          name: action.payload.name,
          buildingId: action.payload.buildingId,
          ownerId: '', 
          description: action.payload.description,
          image: action.payload.image,
          price: action.payload.price,
          noOfFloors: action.payload.noOfFloors,
          floorArea: action.payload.floorArea,
          height: action.payload.height,
          isBuy: false, 
          isFirstTime: false, 
          forSale: action.payload.forSale,
          color: '', 
          location: action.payload.location,
          status: action.payload.status,
        }
      })
      .addCase(generateBid.rejected, (state, action) => {
        state.isLoading = false
        state.error     = action.payload ?? action.error.message!
      })
  },
})

export const { resetPropertiesState } = propertiesSlice.actions
export default propertiesSlice.reducer
